import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Printer, ShieldCheck, FileCheck2, Sun, Landmark, Phone, Globe, Mail } from "lucide-react";
import { FIRM } from "@/lib/firm";

const HERO = "https://images.unsplash.com/photo-1694327671725-e2a81cda3436?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FLYER_CSS = `
  .flyer-wrap{background:#0a140f;min-height:100vh;display:flex;flex-direction:column;align-items:center;padding:28px 16px;}
  .flyer{width:8.5in;min-height:11in;background:#faf7ef;color:#12241c;box-shadow:0 20px 60px rgba(0,0,0,.5);
    display:flex;flex-direction:column;overflow:hidden;font-family:'Manrope',system-ui,sans-serif;position:relative;}
  .fl-hero{position:relative;height:3.05in;background:#0B3D2E;overflow:hidden;}
  .fl-hero img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:.42;}
  .fl-hero-shade{position:absolute;inset:0;background:linear-gradient(120deg,#063024 15%,rgba(6,48,36,.55) 70%,rgba(6,48,36,.2));}
  .fl-hero-inner{position:relative;padding:34px 40px;color:#F3EFE0;height:100%;display:flex;flex-direction:column;justify-content:center;}
  .fl-eyebrow{font-size:11px;letter-spacing:.26em;text-transform:uppercase;color:#E4C97A;font-weight:800;}
  .fl-brand{font-size:13px;letter-spacing:.1em;color:#cfe0d7;margin-top:4px;text-transform:uppercase;font-weight:700;}
  .fl-h1{font-family:Georgia,'Times New Roman',serif;font-size:40px;line-height:1.02;margin:12px 0 8px;color:#fff;max-width:6in;font-weight:700;}
  .fl-sub{font-size:14.5px;line-height:1.5;color:#e7f3ee;max-width:5.4in;}
  .fl-gold-rule{height:5px;background:linear-gradient(90deg,#E4C97A,#B8860B 55%,#E4C97A);}
  .fl-body{padding:26px 40px 10px;display:flex;flex-direction:column;flex:1;}
  .fl-lead{font-size:14px;line-height:1.55;color:#24382f;}
  .fl-lead b{color:#0B3D2E;}
  .fl-benefits{display:grid;grid-template-columns:1fr 1fr;gap:14px 26px;margin:20px 0 6px;}
  .fl-ben{display:flex;gap:12px;align-items:flex-start;}
  .fl-ben .ic{flex:0 0 38px;height:38px;border-radius:10px;background:#0B3D2E;color:#E4C97A;display:flex;align-items:center;justify-content:center;}
  .fl-ben .tt{font-size:14px;font-weight:800;color:#0B3D2E;}
  .fl-ben .dd{font-size:12px;line-height:1.45;color:#415349;margin-top:2px;}
  .fl-cta{margin-top:auto;display:flex;gap:24px;align-items:center;background:#0B3D2E;border-radius:14px;padding:20px 24px;color:#F3EFE0;}
  .fl-qr{flex:0 0 auto;background:#fff;padding:12px;border-radius:12px;line-height:0;box-shadow:0 6px 20px rgba(0,0,0,.25);}
  .fl-qr img{width:1.55in;height:1.55in;display:block;}
  .fl-cta-txt{flex:1;}
  .fl-scan{font-size:13px;letter-spacing:.22em;text-transform:uppercase;color:#E4C97A;font-weight:800;}
  .fl-cta-h{font-family:Georgia,serif;font-size:25px;margin:4px 0 6px;color:#fff;}
  .fl-url{font-size:13px;color:#cfe0d7;word-break:break-all;}
  .fl-fee{display:inline-flex;gap:14px;align-items:baseline;margin-top:10px;background:rgba(228,201,122,.14);border:1px solid rgba(228,201,122,.5);border-radius:10px;padding:8px 14px;}
  .fl-fee .amt{font-family:Georgia,serif;font-size:22px;color:#E4C97A;font-weight:700;}
  .fl-fee .mo{font-size:12px;color:#e7f3ee;}
  .fl-pay{font-size:11.5px;line-height:1.5;color:#e7f3ee;margin-top:10px;}
  .fl-pay b{color:#E4C97A;}
  .fl-foot{padding:14px 40px 22px;border-top:1px solid #e3ddc9;}
  .fl-contact{display:flex;gap:22px;flex-wrap:wrap;font-size:12.5px;color:#2c4036;font-weight:700;align-items:center;}
  .fl-contact span{display:inline-flex;gap:6px;align-items:center;}
  .fl-disc{font-size:9px;line-height:1.5;color:#7a8a81;margin-top:8px;}
  .fl-print-btn{margin:0 0 18px;display:inline-flex;gap:8px;align-items:center;background:#E4C97A;color:#0a140f;border:none;border-radius:999px;padding:12px 26px;font-weight:800;font-size:13px;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;}
  @media print{
    @page{size:letter;margin:0;}
    body{background:#fff!important;}
    .no-print{display:none!important;}
    .flyer-wrap{padding:0!important;background:#fff!important;}
    .flyer{box-shadow:none!important;width:100%!important;min-height:100vh!important;}
    .fl-hero img{opacity:.42!important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .flyer,.fl-hero,.fl-cta,.fl-gold-rule,.fl-ben .ic,.fl-fee{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
  }
`;

const Ben = ({ icon: Icon, tt, dd }) => (
  <div className="fl-ben"><span className="ic"><Icon size={19} /></span><div><div className="tt">{tt}</div><div className="dd">{dd}</div></div></div>
);

export default function TaxFlyer() {
  const enrollUrl = "https://www.seutrading.com/tax-debt-program";
  const [qr, setQr] = useState("");

  useEffect(() => {
    QRCode.toDataURL(enrollUrl, { margin: 1, width: 320, errorCorrectionLevel: "M",
      color: { dark: "#0B3D2E", light: "#ffffff" } })
      .then(setQr).catch(() => setQr(""));
  }, [enrollUrl]);

  return (
    <div className="flyer-wrap">
      <button className="fl-print-btn no-print" onClick={() => window.print()} data-testid="flyer-print">
        <Printer size={16} /> Print / Save as PDF
      </button>

      <div className="flyer" data-testid="tax-flyer">
        <div className="fl-hero">
          <img src={HERO} alt="Solar energy" crossOrigin="anonymous" />
          <div className="fl-hero-shade" />
          <div className="fl-hero-inner">
            <div className="fl-eyebrow">Tax Debt Assistance Program</div>
            <div className="fl-brand">{FIRM.name}</div>
            <h1 className="fl-h1">Owe the IRS?<br />Put a clean‑energy credit to work.</h1>
            <p className="fl-sub">Reduce your federal income‑tax burden with a transferable solar tax credit — and let us prepare the IRS paperwork to address liens, levies and back balances.</p>
          </div>
        </div>
        <div className="fl-gold-rule" />

        <div className="fl-body">
          <p className="fl-lead">
            Under <b>IRC §6418</b>, {FIRM.name} transfers you an eligible <b>§48 solar Investment Tax Credit</b> — drawn from our <b>Master Solar Tax Credit
            Transfer Agreement</b> — that you claim on your federal income tax return (Form 3800) to lower what you owe. We also prepare and file your
            <b> official IRS request forms</b> — all auto‑populated from a short enrollment.
          </p>

          <div className="fl-benefits">
            <Ben icon={Sun} tt="Transferable Solar Credit" dd="A real §6418 clean‑energy credit applied on your return to reduce income tax." />
            <Ben icon={ShieldCheck} tt="Lien & Levy Requests" dd="Form 12277 withdrawal, 911 hardship levy release, installment agreement & more." />
            <Ben icon={FileCheck2} tt="Official IRS Forms" dd="2848, 8821, 9465, 433‑F, 843, 656, 1040‑V — prepared and filed for you." />
            <Ben icon={Landmark} tt="Done‑For‑You Filing" dd="We assemble, e‑sign and submit your package to the IRS for consideration." />
          </div>

          <div className="fl-cta">
            <div className="fl-qr">{qr ? <img src={qr} alt="Scan to enroll" data-testid="flyer-qr" /> : <div style={{ width: "1.55in", height: "1.55in" }} />}</div>
            <div className="fl-cta-txt">
              <div className="fl-scan">Scan to enroll</div>
              <div className="fl-cta-h">Sign up &amp; get started</div>
              <div className="fl-url">{enrollUrl}</div>
              <div className="fl-fee"><span className="amt">$850</span><span className="mo">first $10,000 balance · +$25 per additional $1,000 · +$9.99/mo service fee</span></div>
              <div className="fl-pay">Enroll at the link above, then <b>pay by card (Stripe)</b> at checkout — or pay by <b>Cash&nbsp;App to&nbsp;$seutrade</b> with the note <b>“from [your name]”</b> and email us at <b>seutradeinfo@gmail.com</b>.</div>
            </div>
          </div>
        </div>

        <div className="fl-foot">
          <div className="fl-contact">
            <span><Phone size={14} color="#0B3D2E" /> {FIRM.phone}</span>
            <span><Globe size={14} color="#0B3D2E" /> www.seutrading.com</span>
            <span><Mail size={14} color="#0B3D2E" /> seutrading@outlook.com</span>
            <span><Landmark size={14} color="#0B3D2E" /> {FIRM.address}</span>
          </div>
          <p className="fl-disc">
            {FIRM.name} is a private faith‑based organization (26 U.S.C. § 508(c)(1)(A)). The transferred clean‑energy credit reduces federal
            income‑tax liability on a filed return and is subject to the general business credit, passive‑activity and at‑risk limitations and
            possible §50 recapture. IRS request forms are the taxpayer's own applications submitted for IRS consideration. Nothing herein is
            issued, insured, or guaranteed by the United States, the Treasury, or the Internal Revenue Service, and outcomes depend on IRS
            review and individual circumstances. This is not tax or legal advice.
          </p>
        </div>
      </div>
      <style>{FLYER_CSS}</style>
    </div>
  );
}
